import { useCallback, useEffect } from 'react';
import { initGotchaMetrics, metric } from '@evo/gotcha-log/dist/metrics';

import { isMobile } from '@utils/environment';

type PageName = 'catalog'
| 'profile'
| 'product_old'
| 'product_new';

type GotchaMetricsProps = {
  pageName: PageName
};

type Metric = {
  name: string
  value: number
};

function observerTotalBlockingTime(cb: (arg: Metric) => void) {
  let totalBlockingTime = 0;
  const observer = new PerformanceObserver((list) => {
    const perfEntries = list.getEntries();
    for (const perfEntry of perfEntries) {
      totalBlockingTime += perfEntry.duration - 50;
    }
    cb({
      name: 'TBT',
      value: totalBlockingTime,
    });
    observer.disconnect();
  });
  observer.observe({ type: 'longtask', buffered: true });
}

export const useGotchaWebVitals = ({
  pageName,
}: GotchaMetricsProps) => {
  const sendMetric = useCallback((originalMetric: Metric) => {
    if (process.env.NODE_ENV === 'production') {
      metric(
        originalMetric.name,
        originalMetric.value,
        {
          pageName,
          device: isMobile() ? 'mobile' : 'desktop',
        },
      );
    }

    console.log(`WEB VITALS: ${originalMetric.name} - ${originalMetric.value}`);
  }, [pageName]);

  useEffect(
    () => {
      const initWebVitals = async () => {
        // https://github.com/GoogleChrome/web-vitals/blob/main/docs/upgrading-to-v5.md#more-details-on-the-baseline-widely-available-change
        try {
          const {
            onTTFB,
            onCLS,
            onFCP,
            onINP,
            onLCP,
          } = await import(/* webpackChunkName: "web-vitals" */ 'web-vitals');
  
          onTTFB(sendMetric);
          onCLS(sendMetric);
          onFCP(sendMetric);
          onINP(sendMetric);
          onLCP(sendMetric);
          observerTotalBlockingTime(sendMetric);
        } catch (err) {
          console.log(err);
        }
      };

      if (process.env.NODE_ENV === 'production') {
        initGotchaMetrics({
          project: GOTCHA_PROJECT,
          host: GOTCHA_DOMAIN,
        });
      }

      initWebVitals();
    },
    [sendMetric],
  );

  return null;
};
